import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import {
  CustomImportApiService,
  CustomImportPayload,
  ExportIdTypeDto,
  formatExportIdTypeLabel
} from '../../../services/custom-import-api.service';
import { SessionUser, GLOBAL_DATA_ACCESS_LEVEL } from '../../../services/auth.service';
import { NotificationService } from '../../../services/notification.service';
import { CustomImportView } from '../custom-imports.types';
import { CustomSelectComponent, CustomSelectOption } from '../../shared/custom-select/custom-select.component';

@Component({
  selector: 'app-custom-imports-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule, CustomSelectComponent],
  templateUrl: './custom-imports-form.component.html',
  styleUrls: ['../custom-imports.component.scss']
})
export class CustomImportsFormComponent implements OnChanges {
  @Input() exportIdTypes: ExportIdTypeDto[] = [];
  @Input() currentUser: SessionUser | null = null;
  @Input() mode: 'create' | 'edit' = 'create';
  @Input() initialValue: CustomImportView | null = null;
  private _hasGlobalDataScope = false;
  get hasGlobalDataScope(): boolean { return this._hasGlobalDataScope; }
  @Input() set hasGlobalDataScope(value: boolean) {
    this._hasGlobalDataScope = value;
    const ctrl = this.customImportForm?.get('ownerEntitySelect');
    if (value) {
      ctrl?.setValidators([Validators.required]);
    } else {
      ctrl?.clearValidators();
    }
    ctrl?.updateValueAndValidity({ emitEvent: false });
  }
  @Input() ownerEntityOptions: { value: string; label: string }[] = [];
  // Advanced (Console/admin) mode: currently only unlocks showing the raw internal ID.
  @Input() advanced = false;
  @Output() saved = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();

  formTitleKey = 'customImports.form.createTitle';
  customImportForm: FormGroup;
  saving = false;
  uploadProgress = 0;
  uploading = false;
  fileError: string | null = null;
  selectedFile: File | null = null;
  copied = false;
  copiedPath = false;

  copyId(): void {
    const id = this.customImportForm.value.id;
    if (!id) {
      return;
    }
    navigator.clipboard?.writeText(id).then(() => {
      this.copied = true;
      setTimeout(() => { this.copied = false; }, 1500);
    });
  }

  copyStoragePath(): void {
    const path = this.initialValue?.storagePath;
    if (!path) {
      return;
    }
    navigator.clipboard?.writeText(path).then(() => {
      this.copiedPath = true;
      setTimeout(() => { this.copiedPath = false; }, 1500);
    });
  }

  get hasGlobalDataAccess(): boolean {
    return this.currentUser?.dataAccessLevel === GLOBAL_DATA_ACCESS_LEVEL;
  }

  get exportIdTypeOptions(): CustomSelectOption[] {
    return this.exportIdTypes.map(type => ({
      value: type.ExportIDType,
      label: formatExportIdTypeLabel(type.ExportIDType)
    }));
  }

  constructor(
    private readonly customImportApi: CustomImportApiService,
    private readonly translate: TranslateService,
    private readonly fb: FormBuilder,
    private readonly notificationService: NotificationService
  ) {
    this.customImportForm = this.fb.group({
      id: [''],
      name: ['', Validators.required],
      exportIdType: ['', Validators.required],
      status: [true],
      ownerEntityType: [''],
      ownerEntityId: [''],
      ownerEntitySelect: ['']
    });

    this.customImportForm.get('ownerEntitySelect')?.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((id: string) => {
        if (id) {
          this.customImportForm.patchValue({
            ownerEntityId: id,
            ownerEntityType: 'publisher'
          }, { emitEvent: false });
        }
      });

    // Auto-suggest name from exportIdType (only if name is empty)
    this.customImportForm.get('exportIdType')?.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((exportIdType) => {
        if (exportIdType) {
          const selectedType = this.exportIdTypes.find(type => type.ExportIDType === exportIdType);
          const currentName = this.customImportForm.get('name')?.value;
          // Only auto-fill if name is empty
          if (selectedType && !currentName) {
            this.customImportForm.patchValue({ name: selectedType.Name }, { emitEvent: false });
          }
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['currentUser']) {
      this.setOwnerDefaults();
    }

    if (changes['mode'] || changes['initialValue']) {
      if (this.mode === 'edit' && this.initialValue) {
        this.formTitleKey = 'customImports.form.editTitle';
        this.patchFormFromInitial(this.initialValue);
      } else {
        this.formTitleKey = 'customImports.form.createTitle';
        this.resetFormState();
        this.customImportForm.reset(this.createEmptyFormValue());
      }
    }
  }

  async submit(): Promise<void> {
    if (this.customImportForm.invalid) {
      this.customImportForm.markAllAsTouched();
      return;
    }
    const formValue = this.customImportForm.getRawValue();
    if (!this.selectedFile && this.mode === 'create') {
      this.fileError = this.translate.instant('customImports.errors.fileRequired');
      return;
    }

    // Ensure name is set from exportIdType
    const selectedType = this.exportIdTypes.find(type => type.ExportIDType === formValue.exportIdType);
    const name = formValue.name || selectedType?.Name || formValue.exportIdType;

    const payload: CustomImportPayload = {
      ID: formValue.id || undefined,
      Name: name,
      Owner: {
        EntityType: formValue.ownerEntityType || this.currentUser?.dataAccessLevel || 'publisher',
        EntityID: formValue.ownerEntityId || this.currentUser?.entityID || ''
      },
      ExportIDType: formValue.exportIdType,
      FileName: this.selectedFile?.name,
      Status: formValue.status ? 1 : 0
    };

    this.saving = true;
    this.fileError = null;
    try {
      const saved = await firstValueFrom(
        this.customImportApi.saveCustomImport(payload, this.mode === 'edit')
      );

      if (this.selectedFile) {
        this.uploading = true;
        await this.customImportApi.uploadFile(
          this.selectedFile,
          saved.ID,
          (progress) => {
            this.uploadProgress = progress;
          }
        );
      }

      this.notificationService.notify('success', this.translate.instant('customImports.messages.saved'));
      this.saved.emit();
      this.resetFormAfterSubmit();
    } catch (error) {
      console.error('[custom-imports] save failed', error);
      const message = (error as Error)?.message || this.translate.instant('customImports.errors.save');
      this.notificationService.notify('error', message);
    } finally {
      this.saving = false;
      this.uploading = false;
      this.uploadProgress = 0;
      this.selectedFile = null;
    }
  }

  cancel(): void {
    if (this.saving || this.uploading) {
      return;
    }
    this.cancelled.emit();
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const target = event.currentTarget as HTMLElement;
    target.classList.add('drag-over');
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const target = event.currentTarget as HTMLElement;
    target.classList.remove('drag-over');
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const target = event.currentTarget as HTMLElement;
    target.classList.remove('drag-over');

    const file = event.dataTransfer?.files?.[0];
    if (file) {
      void this.processFile(file);
    }
  }

  async handleFileSelected(event: Event): Promise<void> {
    const target = event.target as HTMLInputElement | null;
    const file = target?.files?.[0];
    if (!file) {
      return;
    }

    await this.processFile(file);

    // Clear the file input
    if (target) {
      target.value = '';
    }
  }

  // Custom imports accept a single-column file only: one ExportIDValue per line, no header,
  // no delimiter. If the first line looks like a header (not shaped like an ID value), it's
  // stripped here — the backend treats every non-empty line as a real value, so a leftover
  // header would otherwise be ingested as if it were an ID.
  private async processFile(file: File): Promise<void> {
    const exportIdType = this.customImportForm.value.exportIdType;
    if (!exportIdType) {
      this.fileError = 'Please select an ID Type before uploading a file';
      this.selectedFile = null;
      return;
    }

    try {
      const content = await file.text();
      const lines = content.split(/\r?\n/u);
      const firstNonEmptyIndex = lines.findIndex(line => line.trim());
      const hasHeader = firstNonEmptyIndex >= 0 && this.isHeaderLine(lines[firstNonEmptyIndex]);
      const dataLines = hasHeader ? [...lines.slice(0, firstNonEmptyIndex), ...lines.slice(firstNonEmptyIndex + 1)] : lines;

      const validationError = this.validateFileFormat(dataLines, exportIdType);
      if (validationError) {
        this.fileError = validationError;
        this.selectedFile = null;
        return;
      }

      const normalizedContent = dataLines.join('\n');
      this.selectedFile = normalizedContent === content ? file : new File([normalizedContent], file.name, { type: file.type || 'text/plain' });
      this.fileError = null;
      this.uploadProgress = 0;
    } catch (error) {
      console.error('[custom-imports] file processing failed', error);
      this.fileError = this.translate.instant('customImports.errors.preview');
      this.selectedFile = null;
    }
  }

  private isHeaderLine(line: string): boolean {
    const trimmed = line.trim();
    if (!trimmed) {
      return false;
    }
    return !this.looksLikeIdValue(trimmed);
  }

  private looksLikeIdValue(value: string): boolean {
    if (!value) {
      return false;
    }
    const normalized = value.trim();
    const uuidLike = /^[0-9a-fA-F-]{8,}$/u;
    const numeric = /^[0-9]+$/u;
    return uuidLike.test(normalized) || numeric.test(normalized);
  }

  private validateFileFormat(lines: string[], exportIdType: string): string | null {
    try {
      const dataLines = lines.map(line => line.trim()).filter(Boolean);
      if (!dataLines.length) {
        return 'File must contain at least one data row';
      }

      let validationRegex: RegExp | null = null;
      let formatDescription = '';

      switch (exportIdType.toUpperCase()) {
        case 'TNCID':
        case 'MAID':
          validationRegex = /^[a-z0-9]{8}-[a-z0-9]{4}-[a-z0-9]{4}-[a-z0-9]{4}-[a-z0-9]{12}$/i;
          formatDescription = 'UUID format (xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)';
          break;
        case 'EMAIL_SHA256':
        case 'EMAIL-SHA256':
        case 'VAT_ID':
          validationRegex = /^[a-z0-9]{64}$/i;
          formatDescription = 'SHA256 hash format (64 hexadecimal characters)';
          break;
        case 'EMAIL_MD5':
        case 'EMAIL-MD5':
          validationRegex = /^[a-z0-9]{32}$/i;
          formatDescription = 'MD5 hash format (32 hexadecimal characters)';
          break;
        default:
          // No validation for other types
          return null;
      }

      if (!validationRegex) {
        return null;
      }

      // Validate the first 5 data rows
      const sample = dataLines.slice(0, 5);
      const invalidRows: number[] = [];

      sample.forEach((value, i) => {
        if (value && !validationRegex.test(value)) {
          invalidRows.push(i + 1);
        }
      });

      if (invalidRows.length > 0) {
        return `Invalid format for ${exportIdType}. Expected ${formatDescription}. Invalid values found at rows: ${invalidRows.join(', ')}`;
      }

      return null;
    } catch (error) {
      console.error('[custom-imports] format validation failed', error);
      return null;
    }
  }

  private createEmptyFormValue(): Record<string, unknown> {
    return {
      id: '',
      name: '',
      exportIdType: '',
      status: true,
      ownerEntityType: this.currentUser?.dataAccessLevel || 'publisher',
      ownerEntityId: this.currentUser?.entityID || '',
      ownerEntitySelect: this.currentUser?.entityID || ''
    };
  }

  private patchFormFromInitial(item: CustomImportView): void {
    this.resetFormState();
    this.customImportForm.reset({
      id: item.id,
      name: item.name,
      exportIdType: item.exportIdType,
      status: item.status === 'active',
      ownerEntityType: item.ownerEntityType || this.currentUser?.dataAccessLevel || 'publisher',
      ownerEntityId: item.ownerEntityId || this.currentUser?.entityID || '',
      ownerEntitySelect: item.ownerEntityId || this.currentUser?.entityID || ''
    });
  }

  private resetFormState(): void {
    this.uploadProgress = 0;
    this.uploading = false;
    this.fileError = null;
    this.selectedFile = null;
  }

  private resetFormAfterSubmit(): void {
    this.formTitleKey = 'customImports.form.createTitle';
    this.customImportForm.reset(this.createEmptyFormValue());
    this.resetFormState();
  }

  private setOwnerDefaults(): void {
    if (!this.currentUser) {
      return;
    }
    const ownerEntityType = this.currentUser.dataAccessLevel || 'publisher';
    const ownerEntityId = this.currentUser.entityID || '';
    this.customImportForm.patchValue({
      ownerEntityType,
      ownerEntityId
    }, { emitEvent: false });
  }
}
